'use client'

import { removeSeat } from '@/app/order/[performanceId]/seat-map/action'
import { removeConcession } from '@/app/order/checkout/action'
import { useCart } from '@/hooks/useCart'
import type { IFormattedOrder } from '@a24/av-sdk/order-sdk'
import { useTransition } from 'react'
import CartLine from './cart-line'
import CartShowCard from './cart-show-card'
import type { SanityCartPerformance } from './type'

interface IProps {
  serviceCharges?: IFormattedOrder['serviceCharges']
  cartItems: IFormattedOrder['cartItems']
  concessionItems: IFormattedOrder['concessionCartItems']
  cartPerformanceData: SanityCartPerformance[]
}

const CartItems = ({
  cartItems,
  serviceCharges,
  concessionItems,
  cartPerformanceData
}: IProps) => {
  const { decrementOrderItemCount } = useCart()
  const performanceDataMap = new Map<string, SanityCartPerformance>()

  // biome-ignore lint/complexity/noForEach: <explanation>
  cartPerformanceData.forEach((performance) => {
    performanceDataMap.set(performance.id, performance)
  })

  const [isLoading, startTransition] = useTransition()

  return (
    <div className='pb-32 grid gap-6 pt-6'>
      {cartItems.map((item) => (
        <div key={item.performanceId} className='mt-8'>
          <CartShowCard
            performanceData={performanceDataMap.get(item.performanceId)}
          />
          <div className='grid gap-1'>
            {item.admissions.map((line) => (
              <CartLine
                key={line.id}
                seatNumber={line.seat}
                seatRow={line.row}
                lineId={line.priceValueId}
                linePrice={Number(line.price)}
                lineTitle={line.legend?.description || 'Item'}
                lineUnitPrice={0}
                onRemove={() => {
                  startTransition(async () => {
                    if (line.id) {
                      await removeSeat({
                        seatId: line.id,
                        performanceId: item.performanceId
                      })
                      decrementOrderItemCount()
                    }
                  })
                }}
              />
            ))}
            {isLoading && 'Loading...'}

            {Number(serviceCharges || '0') > 0 && (
              <CartLine
                lineTitle='Service fees'
                lineUnitPrice={0}
                linePrice={Number(serviceCharges || '0')}
                isServiceFee={true}
              />
            )}
          </div>
        </div>
      ))}
      {concessionItems.length > 0 && (
        <div className='title-md text-heritage-red-full mt-8'>Concessions</div>
      )}
      <div className='grid gap-1'>
        {concessionItems.map((line) => (
          <CartLine
            key={line.id}
            lineId={line.id}
            linePrice={Number(line.price)}
            lineTitle={line.name || 'Item'}
            lineUnitPrice={0}
            onRemove={() => {
              startTransition(async () => {
                if (line.id) {
                  await removeConcession({
                    concessionId: line.id
                  })
                  decrementOrderItemCount()
                }
              })
            }}
          />
        ))}
      </div>
    </div>
  )
}

export default CartItems
